import { Input } from "components";
import ETLogo from '../../../../assets/environment-testing.png';
import React, { useState } from 'react';
import { useNavigate } from "react-router";
import { sendOtpToRegisterClient } from 'services';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Typography, Snackbar, Alert, Link, Paper, Container } from '@mui/material';

interface FormData {
    email: string;
}

interface FormErrors {
    email?: string;
}

const EMAIL_MAX_LENGTH = 50;
const EMAIL_MAX_LENGTH_ERROR = 'Email Address cannot exceed 50 characters.';

export const AccountLogin = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<FormData>({
        email: ''
    });
    const [errors, setErrors] = useState<FormErrors>({});
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success' as 'success' | 'error'
    });

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        if (value.length > EMAIL_MAX_LENGTH) {
            setErrors({
                email: EMAIL_MAX_LENGTH_ERROR,
            });
            return;
        }

        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));

        setErrors({
            email: undefined,
        });
    };

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};

        if (!formData.email.trim()) {
            newErrors.email = 'Email Address is required';
        } else if (formData.email.trim().length > EMAIL_MAX_LENGTH) {
            newErrors.email = EMAIL_MAX_LENGTH_ERROR;
        } else if (!validateEmail(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const response = await sendOtpToRegisterClient(formData.email);

            if (response.status === 200) {
                setSnackbar({
                    open: true,
                    message: 'OTP sent successfully! Please check your email.',
                    severity: 'success'
                });

                setTimeout(() => {
                    navigate('/verification-client-otp', {
                        state: { email: formData.email }
                    });
                }, 2000);
            } else {
                setSnackbar({
                    open: true,
                    message: response?.data || 'Something went wrong. Please try again.',
                    severity: 'error'
                });
            }

        } catch (error: any) {
            let errorMessage = 'Failed to send OTP. Please try again.';

            if (error?.response?.status) {
                switch (error.response.status) {
                    case 400:
                        errorMessage = error?.response?.data || 'Invalid email address.';
                        break;
                    case 401:
                        errorMessage = 'Unauthorized access.';
                        break;
                    case 404:
                        errorMessage = 'Service not found.';
                        break;
                    case 500:
                        errorMessage = 'Server error. Please try again later.';
                        break;
                    default:
                        errorMessage = error?.response?.data || 'Something went wrong. Please try again.';
                }
            } else if (error?.message) {
                errorMessage = error.message;
            }

            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error'
            });

        } finally {
            setLoading(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: 'linear-gradient(to right, #003883, #ee7d11)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4
            }}
        >
            <Container maxWidth="sm">
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 3, md: 5 },
                        borderRadius: 3,
                        background: '#ffffff',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06), 0 8px 24px rgba(0, 0, 0, 0.08)',
                        border: '1px solid #e8e8e8',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '5px',
                            background: 'linear-gradient(90deg, #003883 0%, #0056b3 100%)',
                        }
                    }}
                >
                    <Box
                        component="form"
                        onSubmit={handleSubmit}
                        sx={{
                            width: '100%',
                            bgcolor: 'background.paper',
                            borderRadius: 2,
                        }}
                    >
                        <Box sx={{ textAlign: 'center', mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Box sx={{ mb: 2, textAlign: 'center' }}>
                                <Link
                                    href="https://www.eurofins.com.au/environment-testing/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ display: 'inline-block' }}
                                >
                                    <Box
                                        component="img"
                                        src={ETLogo}
                                        alt="Eurofins Environment Testing Logo"
                                        onError={(e) => {
                                            e.currentTarget.style.display = 'none';
                                        }}
                                        sx={{
                                            width: {
                                                xs: '260px', // mobile
                                                sm: '320px', // tablet
                                                md: '450px', // desktop
                                            },
                                            maxWidth: '100%',
                                            height: 'auto',
                                            mb: 3,
                                        }}
                                    />
                                </Link>
                            </Box>
                            <Typography
                                variant="h5"
                                component="h1"
                                sx={{ fontWeight: 600, fontSize: '18px', color: 'secondary.main' }}
                                data-testid="registration-title"
                            >
                                New Account Registration
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 1, fontSize: '12px' }}
                            >
                                Enter your email address to receive an OTP
                            </Typography>
                        </Box>

                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                            <Box sx={{ width: '100%', maxWidth: '350px' }}>
                                <Input
                                    name="email"
                                    type="text"
                                    size="small"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    fullWidth
                                    label="Email Address"
                                    placeholder="Enter your email address"
                                    error={!!errors.email}
                                    helperText={errors.email}
                                    inputProps={{
                                        "data-testid": 'email-field',
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 1,
                                        }
                                    }}
                                />
                            </Box>
                        </Box>

                        <Box sx={{ textAlign: 'center' }}>
                            <LoadingButton
                                type="submit"
                                variant="contained"
                                loading={loading}
                                disabled={loading}
                                size="medium"
                                role="generate-otp"
                                data-testid="generate-otp-button"
                                sx={{
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    fontSize: '12px',
                                    width: 'auto',
                                    px: 4,
                                    minWidth: 0
                                }}
                            >
                                {loading ? 'Sending OTP...' : 'Generate OTP'}
                            </LoadingButton>
                        </Box>

                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                            <Link
                                sx={{
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                }}
                                onClick={() => navigate('/instructions')}
                            >
                                Back to Instructions
                            </Link>
                        </Box>
                    </Box>

                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={6000}
                        onClose={handleCloseSnackbar}
                    >
                        <Alert
                            onClose={handleCloseSnackbar}
                            severity={snackbar.severity}
                            variant="filled"
                            sx={{ width: '100%' }}
                        >
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                </Paper>
            </Container>
        </Box>
    );
};
